import React, { useContext, useCallback, useState, useEffect, useMemo } from 'react';
import { CurrentPageContext, useHistoryState } from 'app/home/general-components/HistoryStackContext';
import { LocalizationContext } from 'app/localization/Context';
import { clearLogFilterSection, countsInLogFilterSections } from 'app/model/Log';
import FullPageTransition from 'app/home/general-components/FullPageTransition';
import { DeviceList, supportedDeviceTypes, supportedRemoteDeviceTypes } from 'app/home/main-pages/devices/Devices';
import { UserList } from '../setting/UsersAndHome';
import { BindList } from 'app/home/main-pages/binds/Binds';
import { ScenarioList } from 'app/home/main-pages/scenarios/Scenarios';
import { SimplifiedRoomList } from '../setting/Room/Rooms';

import { overlayPageAnimation } from '../OverlayPages';

import "@amir04lm26/react-modern-calendar-date-picker/lib/DatePicker.css";
import { Calendar } from "@amir04lm26/react-modern-calendar-date-picker";
import momentj from 'moment-jalaali';

import { ReactComponent as IconDeleteWhite } from 'images/general/delete-white.svg';
import { ReactComponent as IconX } from 'images/general/x.svg';
import { ReactComponent as IconBack } from 'images/general/back.svg';
import { ReactComponent as IconFilterBlack } from 'images/report/filter-black.svg';
import { ReactComponent as IconBackBlack } from 'images/general/back-black.svg';
import { ReactComponent as IconSelectAll } from 'images/general/select-all.svg';
import CustomIconBack from 'app/home/home-components/CustomIconBack';

import { ReactComponent as IconLogUser } from 'images/profile/log.svg';
import { ReactComponent as IconHome } from 'images/general/home-blue.svg';
import { ReactComponent as IconScenario } from 'images/general/scenarios-hinava-color.svg';
import { ReactComponent as IconBinds } from 'images/general/binds-hinava-color.svg';
import { ReactComponent as IconTime } from 'images/general/time-gray.svg';
import { ReactComponent as IconRoom } from 'images/room/yellow/room.svg';

import './FilterReports.scss'


export default function FilterReports({ gateway }) {
    const p = useContext(CurrentPageContext);
    const l = useContext(LocalizationContext);
    const s = l.strings;

    const isVerticalPage = overlayPageAnimation[p.path] === "vertical";

    const logFilter = gateway.logFilter;
    const setLogFilter = gateway.setLogFilter;

    const { delayedCurrentState, pushState, back, setLockState, numberOfStates } =
        useHistoryState(p.id, {
            pageName: "filter-list",
            logFilter: logFilter,
            somethingChanged: false
        });
    const currentState = delayedCurrentState;

    const dismissHandler = useCallback(() => {
        setLockState(true);
        setTimeout(() => {
            back(true);
            setTimeout(() => { // fix a bug in safari: two consecutive backs will result in only perfoming the second one
                p.dismiss();
            });
        });
    }, [back, p, setLockState]);

    const currentLogFilter = currentState.logFilter;
    const setCurrentLogFilter = useCallback((value) => {
        pushState({
            pageName: "filter-list",
            logFilter: value,
            somethingChanged: true
        })
    }, [pushState]);
    const selectPage = useCallback((value) => {
        pushState({
            pageName: value,
            logFilter: currentLogFilter,
            somethingChanged: true
        })
    }, [pushState, currentLogFilter]);

    const saveAndApply = useCallback(() => {
        setLogFilter(currentLogFilter);
        dismissHandler();
    }, [dismissHandler, setLogFilter, currentLogFilter]);
    const clearFilters = useCallback(() => {
        setLogFilter({});
        dismissHandler();
    }, [dismissHandler, setLogFilter]);
    const clearFilter = useCallback((filterName) => {
        clearLogFilterSection(currentLogFilter, setCurrentLogFilter, filterName);
    }, [currentLogFilter, setCurrentLogFilter]);

    const [{ inverse }, setlastnNumberOfStates] = useState({ inverse: false, lastNumberOfStates: 0 });
    useEffect(() => {
        setlastnNumberOfStates((x) => {
            return { inverse: numberOfStates < x.lastNumberOfStates, lastNumberOfStates: numberOfStates }
        })
    }, [numberOfStates]);

    return (
        <div className="filter-reports-page">
            <div className="bg-blank" />
            <div className="bg-secondary-color" />
            <FullPageTransition disableOnFirstTime inverse={inverse}>
                <FilterList
                    negotiatedProtocol={gateway.negotiatedProtocol}
                    saveAndApply={saveAndApply} somethingChanged={currentState.somethingChanged}
                    currentLogFilter={currentLogFilter} selectPage={selectPage}
                    dismissHandler={dismissHandler} clearFilters={clearFilters}
                    isVerticalPage={isVerticalPage}
                    isVisible={currentState.pageName === "filter-list"}
                    key="filter-reports-page-filter-list" />

                <SelectFromDeviceList
                    clearFilter={clearFilter} s={s} back={back}
                    currentLogFilter={currentLogFilter}
                    setCurrentLogFilter={setCurrentLogFilter}
                    devices={gateway.devices}
                    isVisible={currentState.pageName === "device"}
                    key="filter-reports-page-device" />

                <SelectFromUserList
                    clearFilter={clearFilter} l={l} back={back}
                    currentLogFilter={currentLogFilter}
                    setCurrentLogFilter={setCurrentLogFilter}
                    dataFetched={gateway.dataFetched}
                    joinedUsers={gateway.allUsers.joinedUsers}
                    currentUserId={gateway.currentUserId}
                    isVisible={currentState.pageName === "user"}
                    key="filter-reports-page-user" />

                <SelectFromRoomList
                    clearFilter={clearFilter} s={s} back={back}
                    currentLogFilter={currentLogFilter}
                    setCurrentLogFilter={setCurrentLogFilter}
                    dataFetched={gateway.dataFetched}
                    roomsData={gateway.roomsData}
                    isVisible={currentState.pageName === "room"}
                    key="filter-reports-page-room" />

                <SelectTime
                    clearFilter={clearFilter} l={l} back={back}
                    currentLogFilter={currentLogFilter}
                    setCurrentLogFilter={setCurrentLogFilter}
                    isVisible={currentState.pageName === "time"}
                    key="filter-reports-page-time"
                />

                <SelectFromBindList
                    clearFilter={clearFilter} s={s} back={back}
                    currentLogFilter={currentLogFilter}
                    setCurrentLogFilter={setCurrentLogFilter}
                    binds={gateway.binds}
                    devices={gateway.devices}
                    scenarios={gateway.scenarios}
                    isVisible={currentState.pageName === "bind"}
                    key="filter-reports-page-bind" />

                <SelectFromScenarioList
                    clearFilter={clearFilter} s={s} back={back}
                    currentLogFilter={currentLogFilter}
                    setCurrentLogFilter={setCurrentLogFilter}
                    scenarios={gateway.scenarios}
                    isVisible={currentState.pageName === "scenario"}
                    key="filter-reports-page-scenario" />

            </FullPageTransition>
        </div>
    )
}

function FilterList({ dismissHandler, currentLogFilter, selectPage,
    clearFilters, negotiatedProtocol, saveAndApply, somethingChanged,
    isVerticalPage }) {
    const l = useContext(LocalizationContext);
    const s = l.strings;
    const lf = s.logFilter;

    const { filterIsEmpty, counts } = useMemo(() =>
        countsInLogFilterSections(currentLogFilter), [currentLogFilter]);
    const filterKeysAndIcons = [
        { filterName: "user", icon: IconLogUser },
        { filterName: "device", icon: IconHome },
        { filterName: "time", icon: IconTime },
        { filterName: "bind", icon: IconBinds },
        { filterName: "scenario", icon: IconScenario },
    ];
    if (negotiatedProtocol.hasRoom) {
        filterKeysAndIcons.push({ filterName: "room", icon: IconRoom })
    }

    // setLogFilter({
    //     user: { id: [123] },
    //     device: { mac: [1, 2] },
    //     bind: { id: [1, 2, 5, 6, 7, 8, 9, 4, 23] },
    //     scenario: { id: [123] },
    //     room: { id: [123] },
    //     time: {
    //         "start": "2019-04-04 13:25:00",
    //         "end": "2019-08-04 12:20:00"
    //     },
    // })

    return (
        <div className="filter-reports-page-content">
            <div className="nav-button" onClick={dismissHandler} />

            <div className="header-group">
                {isVerticalPage ?
                    <IconX className="x-icon" />
                    : <CustomIconBack className="back-icon" />
                }
                <div />
                {
                    (!filterIsEmpty || somethingChanged) ? (
                        <div className="button-border" onClick={clearFilters} >
                            <div className="label">{lf.clear}</div>
                            <IconDeleteWhite className="icon" />
                        </div>
                    ) : null
                }
            </div>

            <div className="page-header with-margin-bottom">{lf.filters}</div>
            <div className="page-header-info">{lf.filtersInfo}</div>

            <div className="filters" style={(!filterIsEmpty || somethingChanged) ? { marginBottom: 56 } : undefined}>
                {filterKeysAndIcons.map(item =>
                    <div className="row" key={"log-report-item-row-" + item.filterName}
                        onClick={() => selectPage(item.filterName)}>
                        {
                            React.createElement(item.icon, { className: "filter-icon" })
                        }
                        <div className="filter-name">{lf[item.filterName].itemTitle}</div>
                        {
                            counts[item.filterName] > 0 ?
                                <div className="filter-tag active">
                                    {lf[item.filterName].someSelection(l.localizeNumber(counts[item.filterName]))}
                                </div> :
                                <div className="filter-tag inactive">
                                    {lf[item.filterName].noSelection}
                                </div>
                        }
                        <IconBack className="next-icon" />
                    </div>
                )}
            </div>

            {
                (!filterIsEmpty || somethingChanged) ? (
                    <div className="full-blue-button next-button" onClick={saveAndApply}>
                        <div>{lf.saveAndApply}</div> <IconFilterBlack className="icon" />
                    </div>
                ) : null
            }
        </div>
    )
}

function SelectTime({ l, clearFilter, setCurrentLogFilter, currentLogFilter, back }) {
    const s = l.strings;
    const lf = s.logFilter;

    const language = useState(l.fullLanguage)[0];

    const initialTimeFilter = useMemo(() => {
        if (currentLogFilter.time) {
            if (currentLogFilter.time.start && currentLogFilter.time.end) {
                let s = momentj(currentLogFilter.time.start, 'YYYY-MM-DD HH:mm:ss')
                let e = momentj(currentLogFilter.time.end, 'YYYY-MM-DD HH:mm:ss')
                if (language === 'fa') {
                    return {
                        from: { day: s.jDate(), month: s.jMonth() + 1, year: s.jYear() },
                        to: { day: e.jDate(), month: e.jMonth() + 1, year: e.jYear() },
                    }
                } else {
                    return {
                        from: { day: s.date(), month: s.month() + 1, year: s.year() },
                        to: { day: e.date(), month: e.month() + 1, year: e.year() },
                    }
                }
            }
        }
        return {
            from: null,
            to: null
        }
    }, [currentLogFilter, language]);

    const [selectedDayRange, setSelectedDayRange] = useState(initialTimeFilter);
    // {from: {day: 11, month: 10, year: 1402}, to: {day: 21, month: 10, year: 1402}}
    const isRangeValid = selectedDayRange.from || selectedDayRange.to;

    const [startTime, setStartTime] = useState("00:00:00"); // eslint-disable-line no-unused-vars    
    const [endTime, setEndTime] = useState("23:59:59"); // eslint-disable-line no-unused-vars

    const done = useCallback(() => {
        if (isRangeValid) {
            let from = selectedDayRange.from;
            let to = selectedDayRange.to;
            if (!from) {
                from = to;
            } else if (!to) {
                to = from;
            }

            let _startDate =
                String(from.year) + "-" +
                String(from.month) + "-" +
                String(from.day)
            let _endDate =
                String(to.year) + "-" +
                String(to.month) + "-" +
                String(to.day)

            let startDate, endDate
            if (language === 'fa') {
                let s = momentj(_startDate, 'jYYYY-jM-jD')
                startDate = s.format('YYYY-MM-DD')
                let e = momentj(_endDate, 'jYYYY-jM-jD')
                endDate = e.format('YYYY-MM-DD')
            } else {
                let s = momentj(_startDate, 'YYYY-M-D')
                startDate = s.format('YYYY-MM-DD')
                let e = momentj(_endDate, 'YYYY-M-D')
                endDate = e.format('YYYY-MM-DD')
            }

            const newLogFilter = { ...currentLogFilter };
            newLogFilter.time = {
                start: startDate + " " + startTime,
                end: endDate + " " + endTime
            };
            // console.log("newLogFilter.time", newLogFilter.time);
            setCurrentLogFilter(newLogFilter);
        }
    }, [setCurrentLogFilter, currentLogFilter, isRangeValid, language,
        selectedDayRange, startTime, endTime]);

    return (
        <div className="filter-reports-page">
            <div className="bg-blank" />
            <div className="bg-secondary-color" />
            <div className="filter-reports-page-content">
                <div className="header-group">
                    <IconBack className="back-icon" />
                    <div />
                    <div className="button-border" onClick={() => { clearFilter("time") }} >
                        <div className="label">{lf.clear}</div>
                        <IconDeleteWhite className="icon" />
                    </div>
                </div>
                <div className="nav-button" onClick={() => { back(); }} />
                <div className="page-header with-margin-bottom">{lf.time.pageTitle}</div>
                <div className="page-header-info" style={{ marginBottom: 16 }}>{lf.time.pageInfo}</div>

                <Calendar
                    locale={language === 'fa' ? "fa" : undefined}
                    value={selectedDayRange}
                    onChange={setSelectedDayRange}
                    calendarClassName="custom-calendar"
                    calendarSelectedDayClassName="custom-selected"
                    calendarRangeStartClassName="custom-start"
                    calendarRangeBetweenClassName="custom-between"
                    calendarRangeEndClassName="custom-end" />

                {!(isRangeValid) ? <div className="error-label-for-full-button">{lf.time.youMustEnterATimeFrame}</div> : null}
                <div className="full-blue-button next-button" onClick={done} style={{ opacity: (isRangeValid) ? 1 : 0.3 }}>
                    <div>{s.next}</div> <IconBackBlack className="icon-180" />
                </div>
            </div>
        </div>
    );
}

// toggle the selection of `element`. 
// then make the selection of `followedElements` same as the selection of `element`.
function toggleSelectionInAList(list, element, keyOfElement, followedElements) {
    if (followedElements === undefined)
        followedElements = []
    const newList = [...list];
    const index = newList.findIndex((v) => v === element[keyOfElement]);
    if (index >= 0) {
        newList.splice(index, 1);
        // remove all followedElements
        for (let i = 0; i < followedElements.length; i++) {
            const followedElement = followedElements[i];
            const index2 = newList.findIndex((v) => v === followedElement[keyOfElement]);
            if (index2 >= 0) {
                newList.splice(index2, 1);
            }
        }
    } else {
        newList.push(element[keyOfElement]);
        // add all followedElements
        for (let i = 0; i < followedElements.length; i++) {
            const followedElement = followedElements[i];
            const index2 = newList.findIndex((v) => v === followedElement[keyOfElement]);
            if (!(index2 >= 0)) {
                newList.push(followedElement[keyOfElement]);
            }
        }
    }
    return newList;
}

function SelectFromDeviceList({ s, clearFilter, back, currentLogFilter, setCurrentLogFilter, devices }) {
    const [newLogFilter, setNewLogFilter] = useState(currentLogFilter);
    const selectedList = (newLogFilter.device && newLogFilter.device.mac) ? newLogFilter.device.mac : [];

    const select = useCallback((device) => {
        let list = [];
        if (newLogFilter.device)
            if (newLogFilter.device.mac)
                list = newLogFilter.device.mac;
        const newList = toggleSelectionInAList(list, device, "mac", device.sub_devices);
        const _newLogFilter = { ...newLogFilter }
        _newLogFilter.device = { mac: newList };
        setNewLogFilter(_newLogFilter);
    }, [newLogFilter]);
    const selectAll = useCallback(() => {
        const macs = [];
        const supportedDevices = devices.filter(d => supportedDeviceTypes.includes(d.type));

        for (let i = 0; i < supportedDevices.length; i++) {
            const device = supportedDevices[i];
            macs.push(device.mac);
            if (device.sub_devices) {
                const subDevices = device.sub_devices.filter(d => supportedRemoteDeviceTypes.includes(d.type));
                for (let j = 0; j < subDevices.length; j++) {
                    const subDevice = subDevices[j];
                    macs.push(subDevice.mac);
                }
            }
        }
        const _newLogFilter = { ...newLogFilter }
        _newLogFilter.device = { mac: macs };
        setNewLogFilter(_newLogFilter);
    }, [devices, newLogFilter]);
    const done = useCallback(() => {
        setCurrentLogFilter(newLogFilter);
    }, [setCurrentLogFilter, newLogFilter]);

    return (
        <SelectFromList
            clearFilter={clearFilter} s={s} back={back}
            filterName={"device"} done={done} selectAll={selectAll}>
            <DeviceList
                devices={devices}
                selectedDevices={selectedList}
                selectionCallback={select}
                showStates={false}
                dontOpenSettings={true}
                showSubDevices={false}
            />
        </SelectFromList>
    )
}

function SelectFromBindList({ s, clearFilter, back, currentLogFilter, setCurrentLogFilter,
    binds, scenarios, devices }) {
    const [newLogFilter, setNewLogFilter] = useState(currentLogFilter);
    const selectedList = (newLogFilter.bind && newLogFilter.bind.id) ? newLogFilter.bind.id : [];

    const select = useCallback((bind) => {
        let list = [];
        if (newLogFilter.bind)
            if (newLogFilter.bind.id)
                list = newLogFilter.bind.id;
        const newList = toggleSelectionInAList(list, bind, "id");
        const _newLogFilter = { ...newLogFilter }
        _newLogFilter.bind = { id: newList };
        setNewLogFilter(_newLogFilter);
    }, [newLogFilter]);
    const selectAll = useCallback(() => {
        const ids = [];
        for (let i = 0; i < binds.length; i++) {
            const bind = binds[i];
            ids.push(bind.id);
        }
        const _newLogFilter = { ...newLogFilter }
        _newLogFilter.bind = { id: ids };
        setNewLogFilter(_newLogFilter);
    }, [binds, newLogFilter]);
    const done = useCallback(() => {
        setCurrentLogFilter(newLogFilter);
    }, [setCurrentLogFilter, newLogFilter]);

    return (
        <SelectFromList
            clearFilter={clearFilter} s={s} back={back}
            filterName={"bind"} done={done} selectAll={selectAll}>
            <BindList
                scenarios={scenarios}
                devices={devices}
                binds={binds}
                selectionCallback={select} dontShowEnables
                selectedBinds={selectedList}
            />
        </SelectFromList>
    )
}

function SelectFromScenarioList({ s, clearFilter, back, currentLogFilter, setCurrentLogFilter, scenarios }) {
    const [newLogFilter, setNewLogFilter] = useState(currentLogFilter);
    const selectedList = (newLogFilter.scenario && newLogFilter.scenario.id) ? newLogFilter.scenario.id : [];

    const select = useCallback((scenario) => {
        let list = [];
        if (newLogFilter.scenario)
            if (newLogFilter.scenario.id)
                list = newLogFilter.scenario.id;
        const newList = toggleSelectionInAList(list, scenario, "id");
        const _newLogFilter = { ...newLogFilter }
        _newLogFilter.scenario = { id: newList };
        setNewLogFilter(_newLogFilter);
    }, [newLogFilter]);
    const selectAll = useCallback(() => {
        const ids = [];
        for (let i = 0; i < scenarios.length; i++) {
            const scenario = scenarios[i];
            ids.push(scenario.id);
        }
        const _newLogFilter = { ...newLogFilter }
        _newLogFilter.scenario = { id: ids };
        setNewLogFilter(_newLogFilter);
    }, [scenarios, newLogFilter]);
    const done = useCallback(() => {
        setCurrentLogFilter(newLogFilter);
    }, [setCurrentLogFilter, newLogFilter]);

    return (
        <SelectFromList
            clearFilter={clearFilter} s={s} back={back}
            filterName={"scenario"} done={done} selectAll={selectAll}>
            <ScenarioList
                scenarios={scenarios}
                cantRun={false}
                selectionCallback={select} dontShowEnables
                selectedScenarios={selectedList}
                marginTop={0} marginLeft={0} marginRight={0} />
        </SelectFromList>
    )
}

function SelectFromRoomList({ s, clearFilter, back, currentLogFilter, setCurrentLogFilter, dataFetched, roomsData }) {
    const [newLogFilter, setNewLogFilter] = useState(currentLogFilter);
    const selectedList = (newLogFilter.room && newLogFilter.room.id) ? newLogFilter.room.id : [];

    const select = useCallback((room) => {
        let list = [];
        if (newLogFilter.room)
            if (newLogFilter.room.id)
                list = newLogFilter.room.id;
        const newList = toggleSelectionInAList(list, room, "room_id");
        const _newLogFilter = { ...newLogFilter }
        _newLogFilter.room = { id: newList };
        setNewLogFilter(_newLogFilter);
    }, [newLogFilter]);
    const selectAll = useCallback(() => {
        const ids = Object.keys(roomsData.rooms).map((strId) => parseInt(strId));
        const _newLogFilter = { ...newLogFilter }
        _newLogFilter.room = { id: ids };
        setNewLogFilter(_newLogFilter);
    }, [roomsData.rooms, newLogFilter]);
    const done = useCallback(() => {
        setCurrentLogFilter(newLogFilter);
    }, [setCurrentLogFilter, newLogFilter]);

    return (
        <SelectFromList
            clearFilter={clearFilter} s={s} back={back}
            filterName={"room"} done={done} selectAll={selectAll}>
            <SimplifiedRoomList
                s={s} dataFetched={dataFetched} roomsData={roomsData}
                onClick={select}
                selectedRooms={selectedList} />
        </SelectFromList>
    )
}

function SelectFromUserList({
    l, clearFilter, back, currentLogFilter, setCurrentLogFilter,
    dataFetched, joinedUsers, currentUserId }) {
    const s = l.strings;

    const [newLogFilter, setNewLogFilter] = useState(currentLogFilter);
    const selectedList = (newLogFilter.user && newLogFilter.user.id) ? newLogFilter.user.id : [];

    const select = useCallback((user) => {
        let list = [];
        if (newLogFilter.user)
            if (newLogFilter.user.id)
                list = newLogFilter.user.id;
        const newList = toggleSelectionInAList(list, user, "user_id");
        const _newLogFilter = { ...newLogFilter }
        _newLogFilter.user = { id: newList };
        setNewLogFilter(_newLogFilter);
    }, [newLogFilter]);
    const selectAlls = useCallback(() => {
        const ids = [];
        for (let i = 0; i < joinedUsers.length; i++) {
            const user = joinedUsers[i];
            ids.push(user.user_id);
        }
        const _newLogFilter = { ...newLogFilter }
        _newLogFilter.user = { id: ids };
        setNewLogFilter(_newLogFilter);
    }, [joinedUsers, newLogFilter]);
    const done = useCallback(() => {
        setCurrentLogFilter(newLogFilter);
    }, [setCurrentLogFilter, newLogFilter]);

    return (
        <SelectFromList
            clearFilter={clearFilter} s={s} back={back}
            filterName={"user"} done={done} selectAll={selectAlls}>
            <UserList
                l={l} joinedUsers={joinedUsers} showNextIcon={false}
                dataFetched={dataFetched} currentUserId={currentUserId}
                onUserClick={select} selectedUserIds={selectedList}
            />
        </SelectFromList>
    )
}

function SelectFromList({ filterName, s, clearFilter, done, children, back, selectAll }) {
    const lf = s.logFilter;

    return (
        <div className="filter-reports-page">
            <div className="bg-blank" />
            <div className="bg-secondary-color" />
            <div className="filter-reports-page-content">
                <div className="header-group">
                    <IconBack className="back-icon" />
                    <div />
                    <div className="button-border" onClick={() => { clearFilter(filterName) }} >
                        <div className="label">{lf.clear}</div>
                        <IconDeleteWhite className="icon" />
                    </div>
                    <div className="button-border" onClick={selectAll} >
                        <div className="label">{lf.selectAll}</div>
                        <IconSelectAll className="icon" />
                    </div>
                </div>
                <div className="nav-button" onClick={() => { back(); }} />
                <div className="page-header with-margin-bottom">{lf[filterName].pageTitle}</div>
                <div className="page-header-info" style={{ marginBottom: 16 }}>{lf[filterName].pageInfo}</div>

                <div className="the-rest-of-page-container with-full-button-margin">
                    {children}
                </div>

                <div className="full-blue-button next-button" onClick={done}>
                    <div>{s.next}</div> <IconBackBlack className="icon-180" />
                </div>
            </div>
        </div>
    );
}
